import React, { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, History, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiDel, apiGet, apiPost } from '../lib/api.js';
import { useAuth } from '../store/auth.js';
import { getSocket } from '../lib/socket.js';
import { DEFAULT_LIMIT, callsToCsv, presetRange } from '../lib/callHistory.js';
import LeadDrawer from '../components/LeadDrawer.jsx';
import CallRow, { ROW_GRID } from '../components/callHistory/CallRow.jsx';
import CallHistoryControls from '../components/callHistory/CallHistoryControls.jsx';
import ConfirmDeleteModal from '../components/callHistory/ConfirmDeleteModal.jsx';

const LIVE_EVENTS = ['call:logged', 'phone:ended', 'phone:incoming', 'powerdial:session:completed', 'powerdial:agent_hangup'];
const KEY = 'call-history-v2';

const initialFilters = () => ({
  agentIds: ['me'], limit: DEFAULT_LIMIT, direction: '', preset: 'month', ...presetRange('month'),
});

/** Apply a filter patch; choosing DAY/WEEK/MONTH recomputes the date bounds. */
function applyPatch(prev, patch) {
  const next = { ...prev, ...patch };
  if (['day', 'week', 'month'].includes(patch.preset)) Object.assign(next, presetRange(patch.preset));
  return next;
}

/**
 * Call History: filterable, cursor-paged list of calls with recordings.
 * @returns {JSX.Element}
 */
export default function CallHistory() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [filters, setFilters] = useState(initialFilters);
  const [selected, setSelected] = useState(() => new Set());
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [drawerLeadId, setDrawerLeadId] = useState(null);

  const { data: agentsData } = useQuery({ queryKey: ['agents', false], queryFn: () => apiGet('/agents') });
  const agents = (agentsData?.agents || []).filter((a) => String(a._id) !== String(user?._id));

  const params = useMemo(() => {
    const p = { limit: filters.limit };
    if (filters.agentIds.length) p.agentId = filters.agentIds.join(',');
    if (filters.direction) p.direction = filters.direction;
    if (filters.startDate) p.startDate = filters.startDate;
    if (filters.endDate) p.endDate = filters.endDate;
    if (filters.startDate || filters.endDate) p.range = 'custom';
    return p;
  }, [filters]);

  const { data, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } = useInfiniteQuery({
    queryKey: [KEY, params],
    queryFn: ({ pageParam }) => apiGet('/phone/call-history', pageParam ? { ...params, cursor: pageParam } : params),
    initialPageParam: null,
    getNextPageParam: (last) => last?.data?.nextCursor || undefined,
    staleTime: 0,
  });
  const items = useMemo(() => (data?.pages || []).flatMap((p) => p?.data?.items || []), [data]);

  // Live refresh when calls start/end elsewhere in the app.
  useEffect(() => {
    const sock = getSocket();
    if (!sock) return undefined;
    const refresh = () => qc.invalidateQueries({ queryKey: [KEY] });
    LIVE_EVENTS.forEach((e) => sock.on(e, refresh));
    return () => LIVE_EVENTS.forEach((e) => sock.off(e, refresh));
  }, [qc]);

  // Selection never outlives the rows it points at.
  useEffect(() => { setSelected(new Set()); }, [params]);

  const onChange = (patch) => setFilters((f) => applyPatch(f, patch));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allSelected = items.length > 0 && selected.size === items.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((c) => c._id)));

  function downloadCsv() {
    const rows = selected.size ? items.filter((c) => selected.has(c._id)) : items;
    if (!rows.length) { toast.error('No calls to export'); return; }
    const url = URL.createObjectURL(new Blob([callsToCsv(rows)], { type: 'text/csv' }));
    const a = Object.assign(document.createElement('a'), { href: url, download: 'call-history.csv' });
    a.click();
    URL.revokeObjectURL(url);
  }

  async function deleteRecording(callId) {
    setDeletingId(callId);
    try {
      await apiDel(`/phone/calls/${callId}/recording`);
      toast.success('Recording deleted');
      await qc.invalidateQueries({ queryKey: [KEY] });
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not delete recording');
    } finally { setDeletingId(null); }
  }

  async function bulkDelete({ deleteLeads }) {
    setBusy(true);
    try {
      const res = await apiPost('/phone/calls/bulk-delete', { ids: [...selected], deleteLeads });
      const d = res?.data || {};
      toast.success(`Deleted ${d.deletedCalls ?? 0} call(s)${deleteLeads ? ` and ${d.deletedLeads ?? 0} lead(s)` : ''}`);
      if (d.skippedCalls || d.skippedLeads) toast(`Skipped ${(d.skippedCalls || 0) + (d.skippedLeads || 0)} you don't have permission for`);
      setSelected(new Set());
      setConfirming(false);
      await qc.invalidateQueries({ queryKey: [KEY] });
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Bulk delete failed');
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-4 max-w-[1200px] mx-auto">
      <header className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold flex items-center gap-3">
          <History className="w-6 h-6 text-civix-cyan" /> Call History
        </h1>
        <button type="button" onClick={() => refetch()} className="btn-ghost text-xs px-2.5 py-1 inline-flex items-center gap-1.5" title="Refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin text-civix-cyan' : ''}`} /> Refresh
        </button>
      </header>

      <CallHistoryControls
        filters={filters}
        onChange={onChange}
        agents={agents}
        selectedCount={selected.size}
        onDownload={downloadCsv}
        onBulkDelete={() => setConfirming(true)}
      />

      <div className="panel p-0 overflow-hidden">
        <div className={`${ROW_GRID} px-4 py-3 text-base text-white/80 border-b border-white/10`}>
          <span />
          <button type="button" onClick={toggleAll} className="text-left text-violet-300 font-semibold text-sm">
            {allSelected ? '✓ NONE' : '✓ ALL'} <span className="ml-3 text-white/80 font-normal text-base">Phone number</span>
          </button>
          <span>Leads time zone</span>
          <span>Duration</span>
          <span>Date / time</span>
        </div>
        {isLoading && <div className="px-4 py-10 text-center text-white/40">Loading…</div>}
        {!isLoading && items.length === 0 && <div className="px-4 py-12 text-center text-white/50">No calls match these filters.</div>}
        {items.map((c) => (
          <CallRow
            key={c._id}
            call={c}
            selected={selected.has(c._id)}
            onToggle={toggle}
            onOpenLead={setDrawerLeadId}
            onDeleteRecording={deleteRecording}
            deletingId={deletingId}
          />
        ))}
        {hasNextPage && (
          <div className="flex justify-center py-6">
            <button
              type="button"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              aria-label={`Load next ${filters.limit} calls`}
              title={`Load next ${filters.limit} calls`}
              className="w-14 h-14 rounded-full bg-civix-purple hover:bg-violet-500 flex items-center justify-center shadow-lg disabled:opacity-60"
            >
              <ArrowDown className={`w-6 h-6 ${isFetchingNextPage ? 'animate-bounce' : ''}`} />
            </button>
          </div>
        )}
      </div>

      {confirming && <ConfirmDeleteModal count={selected.size} busy={busy} onCancel={() => setConfirming(false)} onConfirm={bulkDelete} />}
      {drawerLeadId && <LeadDrawer leadId={drawerLeadId} onClose={() => setDrawerLeadId(null)} />}
    </div>
  );
}
